import * as R from 'remeda';
import { GridFilterItem } from '@rossum/ui/x-data-grid-pro';
import { CodecFieldType, CodecFieldTypes } from './constants';
import { parseMqlCondition } from './helpers';
import { decodeValue, inferFieldType } from './transformers';
import { ApiConditionModel, ColumnDecoder, MQLOperators } from './types';

type DataGridOperator = GridFilterItem['operator'];

type OperatorResolver = (fieldType?: CodecFieldType) => DataGridOperator;

// `$emptyOrMissing` is decoded separately: its value (not the field type)
// decides between isEmpty and isNotEmpty.
const EMPTY_OR_MISSING_OPERATOR = '$emptyOrMissing';

const byType =
  (
    map: Partial<Record<CodecFieldType, DataGridOperator>>,
    fallback: DataGridOperator
  ): OperatorResolver =>
  fieldType =>
    fieldType ? (map[fieldType] ?? fallback) : fallback;

const typeSensitiveOperators = {
  $eq: byType({ number: '=', date: 'is', string: 'equals' }, 'equals'),
  $ne: byType({ number: '!=', date: 'not' }, '!='),
  $gt: byType({ date: 'after' }, '>'),
  $gte: byType({ date: 'onOrAfter' }, '>='),
  $lt: byType({ date: 'before' }, '<'),
  $lte: byType({ date: 'onOrBefore' }, '<='),
} satisfies Partial<Record<MQLOperators, OperatorResolver>>;

export const mqlOperatorResolvers = {
  $containsPrefixes: () => 'contains',
  $startsWith: () => 'startsWith',
  $in: () => 'isAnyOf',
  $nin: () => 'notIn',
  ...typeSensitiveOperators,
} satisfies Partial<Record<MQLOperators, OperatorResolver>>;

type OperatorResolvers = Partial<Record<string, OperatorResolver>>;

// Field types are supplied by the caller (derived from its own table columns —
// the single source of truth); the `field.<id>.<type>` suffix is a fallback so
// schema columns don't need to be enumerated.
const resolveFieldType = (
  field: string,
  codecFieldTypes: CodecFieldTypes
): CodecFieldType | undefined =>
  codecFieldTypes[field] ?? inferFieldType(field);

const decodeCondition = (
  condition: Record<string, unknown>,
  codecFieldTypes: CodecFieldTypes,
  operatorResolvers: OperatorResolvers
): GridFilterItem | null => {
  const parsedCondition = parseMqlCondition(condition);
  if (!parsedCondition) return null;

  const { field, mqlOperator, value } = parsedCondition;
  const codecFieldType = resolveFieldType(field, codecFieldTypes);

  if (mqlOperator === EMPTY_OR_MISSING_OPERATOR) {
    return {
      field,
      operator: value === true ? 'isEmpty' : 'isNotEmpty',
      value,
    };
  }

  const resolver = operatorResolvers[mqlOperator];
  if (!resolver) return null;

  if (mqlOperator in typeSensitiveOperators && codecFieldType === undefined)
    // oxlint-disable-next-line no-console
    console.warn(
      `[mql] "${field}" uses "${mqlOperator}" with an unresolved field type; decoded as a number/string comparison. If it is a date, pass codecFieldTypes["${field}"] = 'date'.`
    );

  return {
    field,
    operator: resolver(codecFieldType),
    value: decodeValue(value, { field, codecFieldType }),
  };
};

type MergedColumns = {
  items: GridFilterItem[];
  unclaimedConditions: ApiConditionModel['$and'];
};

/**
 * Runs every decoder in `columnDecoders`, one after another. A decoder reads the
 * conditions the previous one left over, takes the ones it understands, and
 * turns them into filter items.
 */
const mergeColumnConditions = (
  conditions: ApiConditionModel['$and'],
  columnDecoders: Record<string, ColumnDecoder>
): MergedColumns =>
  R.entries(columnDecoders).reduce<MergedColumns>(
    (acc, [field, columnDecoder]) => {
      const { items, unclaimedConditions } = columnDecoder(
        acc.unclaimedConditions
      );

      return {
        items: [...acc.items, ...items.map(item => ({ ...item, field }))],
        unclaimedConditions,
      };
    },
    { items: [], unclaimedConditions: conditions }
  );

/**
 * Transform a stored MQL query (as produced by `encodeMqlQuery`) back into a
 * DataGrid filter model so it can be applied to the grid / written to the URL.
 *
 * By default one condition becomes one filter item for the column named by the
 * condition's field. A column stored in some other shape needs its own
 * `ColumnDecoder` in `columnDecoders`, keyed by the column name — the
 * counterpart of `encodeMqlQuery`'s `columnEncoders`. Those run first, so their
 * items come first in the result; the order carries no meaning, since `$and`
 * applies every condition anyway.
 */
export const decodeMqlQuery = (
  query: ApiConditionModel,
  options: {
    codecFieldTypes: CodecFieldTypes;
    columnDecoders?: Record<string, ColumnDecoder>;
    operatorResolvers?: OperatorResolvers;
  }
): GridFilterItem[] => {
  const { codecFieldTypes } = options;
  const operatorResolvers = options.operatorResolvers ?? mqlOperatorResolvers;

  const conditions = (Array.isArray(query?.$and) ? query.$and : []).filter(
    condition => R.isPlainObject(condition)
  );

  const { items, unclaimedConditions } = mergeColumnConditions(
    conditions,
    options.columnDecoders ?? {}
  );

  return [
    ...items,
    ...unclaimedConditions.flatMap(condition => {
      const item = decodeCondition(
        condition,
        codecFieldTypes,
        operatorResolvers
      );
      return item ? [item] : [];
    }),
  ];
};
